// src/ThreeJs/Trees/TreeCopyPaste.js
import * as THREE from "three";
import { sceneManager } from "../SetUps/SceneManager";
import { TREES, VARS } from "../Global/variables";
import { toast } from "../../lib/toast";
import { createTreeMesh } from "./treeGeometry";

let ghost = null;
let ghostActive = false;

export function isTreePastePreviewActive() {
  return ghostActive;
}

function detectShapeFromTree(tree) {
  const foliage = tree?.userData?.foliage;
  const t = foliage?.geometry?.type || "";
  return t.includes("Sphere") ? "circle" : "cone";
}

function serializeTree(tree) {
  const trunk = tree?.userData?.trunk;
  const foliage = tree?.userData?.foliage;
  if (!tree || !trunk || !foliage) return null;

  return {
    shape: detectShapeFromTree(tree),
    treeType: tree.userData?.treeType,
    groupScale: { x: tree.scale.x, y: tree.scale.y, z: tree.scale.z },
    trunkScale: { x: trunk.scale.x, y: trunk.scale.y, z: trunk.scale.z },
    foliageScale: { x: foliage.scale.x, y: foliage.scale.y, z: foliage.scale.z },
    arrowScale: tree.userData?.arrow ? { x: tree.userData.arrow.scale.x, y: tree.userData.arrow.scale.y, z: tree.userData.arrow.scale.z } : null,
    trunkPosition: { x: trunk.position.x, y: trunk.position.y, z: trunk.position.z },
    foliagePosition: { x: foliage.position.x, y: foliage.position.y, z: foliage.position.z },
    arrowPosition: tree.userData?.arrow ? { x: tree.userData.arrow.position.x, y: tree.userData.arrow.position.y, z: tree.userData.arrow.position.z } : null,
    widthScale: tree.userData?.widthScale ?? tree.scale.x,
    lengthScale: tree.userData?.lengthScale ?? tree.scale.z,
    heightScaleY: tree.userData?.heightScaleY ?? trunk.scale.y,
    locked: !!tree.userData?.locked,
  };
}

function createTreeFromClipboard(data) {
  const tree = createTreeMesh(data.treeType || data.shape);
  const trunk = tree.userData.trunk;
  const foliage = tree.userData.foliage;

  tree.scale.set(data.groupScale.x, data.groupScale.y, data.groupScale.z);
  trunk.scale.set(data.trunkScale.x, data.trunkScale.y, data.trunkScale.z);
  foliage.scale.set(data.foliageScale.x, data.foliageScale.y, data.foliageScale.z);
  if (data.trunkPosition) trunk.position.set(data.trunkPosition.x, data.trunkPosition.y, data.trunkPosition.z);
  if (data.foliagePosition) foliage.position.set(data.foliagePosition.x, data.foliagePosition.y, data.foliagePosition.z);
  if (data.arrowScale) tree.userData.arrow.scale.set(data.arrowScale.x, data.arrowScale.y, data.arrowScale.z);
  if (data.arrowPosition) tree.userData.arrow.position.set(data.arrowPosition.x, data.arrowPosition.y, data.arrowPosition.z);

  tree.position.z = 0;
  tree.userData.groundZ = 0;
  tree.userData.widthScale = data.widthScale ?? tree.scale.x;
  tree.userData.lengthScale = data.lengthScale ?? tree.scale.z;
  tree.userData.heightScaleY = data.heightScaleY ?? trunk.scale.y;
  tree.userData.locked = !!data.locked;
  tree.userData.arrow.visible = false;
  tree.updateMatrixWorld(true);

  return tree;
}

function applyGhostStyle(obj) {
  obj.traverse((o) => {
    if (!o.isMesh) return;
    const m = o.material?.clone?.();
    if (!m) return;
    m.transparent = true;
    m.opacity = 0.35;
    m.depthWrite = false;
    m.wireframe = true;
    o.material = m;
  });
}

function disposeObject(obj) {
  if (!obj) return;
  obj.traverse((o) => {
    if (o.geometry) o.geometry.dispose?.();
    if (o.material) {
      if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose?.());
      else o.material.dispose?.();
    }
  });
}

function getPastePointFromMouse() {
  const scene = sceneManager.scene;
  const camera = sceneManager.camera;
  if (!scene || !camera) return null;

  const ndc = VARS.mouseNDC || { x: 0, y: 0 };
  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera({ x: ndc.x, y: ndc.y }, camera);

  const meshes = VARS.Meshes || [];
  if (meshes.length) {
    const hits = raycaster.intersectObjects(meshes, true);
    if (hits.length) {
      const p = hits[0].point.clone();
      p.z = 0;
      return p;
    }
  }

  const fallback = new THREE.Vector3(0, 0, 0);
  fallback.z = 0;
  return fallback;
}

function updateGhostPosition() {
  if (!ghostActive || !ghost) return;
  const p = getPastePointFromMouse();
  if (!p) return;
  ghost.position.copy(p);
  ghost.position.z = 0;
  ghost.userData.groundZ = 0;
}

function onMouseMoveUpdate() {
  updateGhostPosition();
}

function onCanvasClickPaste(e) {
  if (!ghostActive) return;
  const canvas = sceneManager?.renderer?.domElement;
  if (!canvas) return;

  const target = e.target;
  const clickedCanvas = target === canvas || (canvas.contains && canvas.contains(target));
  if (!clickedCanvas) return;
  if (e.button !== 0) return;

  e.preventDefault();
  pasteCopiedTree();
}

export function copySelectedTree(tree) {
  const data = serializeTree(tree);
  if (!data) return;

  VARS.copiedTree = data;
  toast.success("Tree copied");
  console.log("📋 Copied tree:", data);

  startTreePastePreview();
}

export function startTreePastePreview() {
  const data = VARS.copiedTree;
  const scene = sceneManager.scene;
  if (!data || !scene) return;

  stopTreePastePreview();

  ghost = createTreeFromClipboard(data);
  ghost.name = `TreePasteGhost_${Date.now()}`;
  ghost.userData.__isPasteGhost = true;

  applyGhostStyle(ghost);

  scene.add(ghost);
  ghostActive = true;

  updateGhostPosition();

  window.addEventListener("mousemove", onMouseMoveUpdate);
  window.addEventListener("pointerdown", onCanvasClickPaste, true);
}

export function stopTreePastePreview() {
  ghostActive = false;

  window.removeEventListener("mousemove", onMouseMoveUpdate);
  window.removeEventListener("pointerdown", onCanvasClickPaste, true);

  if (ghost) {
    try {
      ghost.parent?.remove(ghost);
    } catch (e) {}
    disposeObject(ghost);
    ghost = null;
  }
}

export function pasteCopiedTree() {
  const data = VARS.copiedTree;
  const scene = sceneManager.scene;
  if (!data || !scene) return null;

  const p = getPastePointFromMouse();
  if (!p) return null;

  const tree = createTreeFromClipboard(data);
  tree.position.copy(p);
  tree.position.z = 0;
  tree.userData.groundZ = 0;

  scene.add(tree);
  TREES.trees.push(tree);

  // Select it (updates editor)
  VARS.selectedTree = tree;
  window.dispatchEvent(new CustomEvent("TREE_SELECTED", { detail: { tree } }));

  // Stop after one paste (prevents accidental spam)
  stopTreePastePreview();

  return tree;
}
